import React from 'react'
import styled from '@emotion/styled'
import { SummaryLine } from '@mobi/betslip/Components/Common/SummaryLine'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, radius, spacing } from '@mobi/component-library/Foundations'
import { toMoney } from '@mobi/utils/money'
import type { Ticket, TicketStatus } from '@core/Areas/Account/Pages/Activity/types'
import { FixedOddsMarketDisplayNames } from '@core/Areas/RaceCard/constants'
import { Money } from '@core/Components/Text/Money'
import { LegsStatusLine } from './Components/LegsStatusLine'

export const Investment: React.FC<{
  status: TicketStatus
  ticket: Ticket
}> = ({ status, ticket }) => {
  const { investment, payValue: payout, return: projPayout, flexPercent } = ticket

  const hasPayoutFromScratchedOrAbandoned =
    payout > 0 &&
    (status === 'Abandoned' ||
      status === 'Refunded' ||
      status === 'Scratched' ||
      status === 'Partially Refunded')

  const isStartingPriceBet =
    ticket.legs?.length === 1 && ticket.legs[0].betType == FixedOddsMarketDisplayNames.SP

  const bonusType = ticket.investment.bonusType
  const hasBonusAmount = (ticket.investment.bonusBet || 0) > 0
  const hasPartialBonus = investment.total > (investment.bonusBet ?? 0)

  return (
    <WrapperStyled data-testid='Ticket.Investment'>
      <LegsStatusLine ticket={ticket} />

      <SummaryLine
        testId='Ticket.Cost'
        left='Cost'
        right={
          <span data-testid='bet-investment'>
            {!hasBonusAmount ? (
              <Money amount={investment.total} />
            ) : hasPartialBonus ? (
              <>
                <span style={{ minWidth: '8rem' }}>
                  {`${toMoney(investment.total - (investment.bonusBet ?? 0), { decimalPlaces: 2 })} + `}
                </span>
                <InvestmentWrapperStyled>
                  {`${toMoney(hasPartialBonus ? (investment.bonusBet ?? 0) : investment.total, {
                    decimalPlaces: 2,
                  })} Bonus ${bonusType === 'BonusBet' ? 'Bet' : 'Cash'}`}
                </InvestmentWrapperStyled>
              </>
            ) : (
              <InvestmentWrapperStyled>
                Bonus {bonusType === 'BonusBet' ? 'Bet' : 'Cash'}{' '}
                {`${toMoney(hasPartialBonus ? (investment.bonusBet ?? 0) : investment.total, {
                  decimalPlaces: 2,
                })}`}
              </InvestmentWrapperStyled>
            )}
          </span>
        }
      />

      {flexPercent && <SummaryLine testId='Ticket.Flexi' left='Flexi' right={flexPercent} />}

      {(() => {
        if (status === 'NoPayout') return null

        if (status === 'Won' || hasPayoutFromScratchedOrAbandoned) {
          return (
            <SummaryLine
              testId='Ticket.Payout'
              left='Payout'
              right={
                <PayoutStyled>
                  <Icon name='SolidCheckCircle' color={colors.success[400]} size='1.6rem' />
                  <Money amount={payout} prefix='+' />
                </PayoutStyled>
              }
            />
          )
        }

        if (status === 'Cancelled') {
          return (
            <SummaryLine
              testId='Ticket.Payout'
              left='Payout'
              right={<Money amount={investment.total} />}
            />
          )
        }

        if (projPayout && !hasPayoutFromScratchedOrAbandoned && !isStartingPriceBet) {
          return (
            <SummaryLine
              testId='Ticket.PotentialPayout'
              left='Potential Payout'
              right={<Money amount={projPayout} />}
            />
          )
        }

        return null
      })()}
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx2,
})

const PayoutStyled = styled.span({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.smx2,
  color: colors.success[400],
})

const InvestmentWrapperStyled = styled.span({
  display: 'inline-flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: colors.success[600],
  background: colors.celery[50],
  borderRadius: radius.smx1,
  padding: `0 ${spacing.smx2}`,
  width: 'auto',
})
